import * as React from "react";
import PropTypes from "prop-types";

import classNames from "classnames";
import { Icon } from "@lululemon/ui-library";
import { closeIcon } from "@lululemon/ui-icons";
import styles from "./email-banner.module.scss";

import { EmailBannerModal } from "./banner-modal/email-banner-modal";

const DISMISS_ANIMATION_TIME = 500;
const BANNER_REQUIRED_KEYS = [
  "headline",
  "label",
  "modalLandscapeImageUrl",
  "modalPortraitImageUrl",
  "modalTitle",
  "modalBody",
  "modalSubmitButtonLabel",
  "modalFooter",
  "success",
  "errors",
];

const EmailBanner = ({ onDismissBanner, bannerProps, bannerRef }) => {
  const [isEmailBannerModalOpen, setEmailBannerModalOpen] =
    React.useState(false);
  const [hidden, setHidden] = React.useState(false);

  const { headline: bannerHeadline, label: bannerLinkText } = bannerProps;
  const handleDismiss = () => setHidden(true);

  React.useEffect(() => {
    if (hidden) {
      const dismissTimer = setTimeout(() => {
        onDismissBanner();
      }, DISMISS_ANIMATION_TIME);

      return () => clearTimeout(dismissTimer);
    }

    return undefined;
  }, [hidden]);

  const keyMustBeValid = (key) => key in bannerProps && !!bannerProps[key];
  const doesBannerHaveValidProps = BANNER_REQUIRED_KEYS.every(keyMustBeValid);

  if (!doesBannerHaveValidProps) return null;

  return (
    <div ref={bannerRef} data-testid="email-banner">
      <div
        className={classNames(styles.emailBanner, {
          [styles.isHidden]: hidden,
        })}
      >
        <div
          className={classNames(
            "lll-text-body-3",
            styles.bannerContent,
            styles.bannerContentMobile
          )}
        >
          <span className={styles.bannerHeadline}>
            {bannerHeadline}{" "}
            <button
              type="button"
              className={classNames(
                "lll-text-body-3",
                "lll-text-primary",
                styles.bannerSignUpButton,
                styles.underline
              )}
              onClick={() => setEmailBannerModalOpen(true)}
            >
              {bannerLinkText}
            </button>
          </span>
        </div>
        <button
          aria-label="dismiss email banner"
          type="button"
          className="banner-dismiss"
          onClick={handleDismiss}
        >
          <Icon
            className={styles.closeIcon}
            content={closeIcon}
            title="close icon"
          />
        </button>
      </div>
      <EmailBannerModal
        bannerProps={bannerProps}
        isEmailBannerModalOpen={isEmailBannerModalOpen}
        setEmailBannerModalOpen={setEmailBannerModalOpen}
      />
    </div>
  );
};

EmailBanner.propTypes = {
  onDismissBanner: PropTypes.func.isRequired,
  bannerProps: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    modalLandscapeImageUrl: PropTypes.string.isRequired,
    modalPortraitImageUrl: PropTypes.string.isRequired,
    modalTitle: PropTypes.string.isRequired,
    modalBody: PropTypes.string.isRequired,
    modalSubmitButtonLabel: PropTypes.string.isRequired,
    modalFooter: PropTypes.string.isRequired,
    success: PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      button: PropTypes.string.isRequired,
    }),
    errors: PropTypes.shape({
      "empty-email": PropTypes.string.isRequired,
      "invalid-email": PropTypes.string.isRequired,
    }),
  }).isRequired,
  bannerRef: PropTypes.oneOfType([
    PropTypes.element,
    // Either a function
    PropTypes.func,
    PropTypes.object,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.element) }),
  ]),
};

export default EmailBanner;
